import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Trans } from "@lingui/react/macro";
import axios from "../../utils/axios";
import intempusApiTokenImage from "../../assets/intempus-api-token.png";

interface IntempusConnectDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const IntempusConnectDialog: React.FC<IntempusConnectDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const theme = useTheme();
  const [username, setUsername] = useState("");
  const [apiToken, setApiToken] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!username.trim() || !apiToken.trim()) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await axios.post(
        "/v1/integrations/create-integration/",
        JSON.stringify({
          identifier: "intempus",
          secret: `${username.trim()};${apiToken.trim()}`,
          version: "v1.0.0",
        }),
        { withCredentials: true },
      );
      // Success - close dialog and notify parent
      setUsername("");
      setApiToken("");
      onSuccess();
    } catch (err: any) {
      console.error("Failed to connect Intempus:", err);
      setError(
        err.response?.data?.message ||
          "Failed to connect. Please check your credentials and try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setUsername("");
      setApiToken("");
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: theme.spacing(2),
          paddingBottom: theme.spacing(2),
        },
      }}
    >
      <DialogContent>
        <DialogTitle
          sx={{
            color: theme.palette.primary.main,
            paddingLeft: 0,
            paddingTop: 0,
          }}
        >
          <Trans context="Dialog title for connecting Intempus integration">
            Connect to Intempus
          </Trans>
        </DialogTitle>

        <DialogContentText sx={{ marginBottom: theme.spacing(2) }}>
          <Trans context="Guidance text explaining how to connect Intempus">
            To connect your Intempus account, you'll need your username and API
            token. You can find your API token in your Intempus settings.
          </Trans>
        </DialogContentText>

        <Box sx={{ marginBottom: theme.spacing(2) }}>
          <Link
            href="https://intempus.dk/web/settings/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontWeight: "bold" }}
          >
            <Trans context="Link to open Intempus settings page">
              Open Intempus Settings →
            </Trans>
          </Link>
        </Box>

        <Box
          sx={{
            marginBottom: theme.spacing(2),
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.spacing(1),
            overflow: "hidden",
          }}
        >
          <img
            src={intempusApiTokenImage}
            alt="Where to find API token in Intempus settings"
            style={{ width: "100%", display: "block" }}
          />
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", marginBottom: theme.spacing(2) }}
        >
          <Trans context="Caption explaining the screenshot above">
            The screenshot above shows where to find your API token in Intempus.
          </Trans>
        </Typography>

        <TextField
          label={
            <Trans context="Label for username input field">Username</Trans>
          }
          fullWidth
          variant="filled"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          disabled={isSubmitting}
          sx={{ marginBottom: theme.spacing(2) }}
          InputProps={{
            style: {
              borderRadius: theme.spacing(2),
            },
            disableUnderline: true,
          }}
        />

        <TextField
          label={
            <Trans context="Label for API token input field">API Token</Trans>
          }
          fullWidth
          variant="filled"
          value={apiToken}
          onChange={(e) => setApiToken(e.target.value)}
          disabled={isSubmitting}
          type="password"
          InputProps={{
            style: {
              borderRadius: theme.spacing(2),
            },
            disableUnderline: true,
          }}
        />

        {error && (
          <Typography
            color="error"
            variant="body2"
            sx={{ marginTop: theme.spacing(2) }}
          >
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ marginRight: theme.spacing(2) }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={isSubmitting}
          sx={{ borderRadius: theme.spacing(2) }}
        >
          <Trans context="Button to cancel and close the dialog">Cancel</Trans>
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || !username.trim() || !apiToken.trim()}
          sx={{ borderRadius: theme.spacing(2) }}
        >
          {isSubmitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <Trans context="Button to submit credentials and connect">
              Connect
            </Trans>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IntempusConnectDialog;
